import { format } from 'date-fns';
import dayjs from 'dayjs';
import { TFunction } from 'i18next';

import { DATE_TIME_FORMAT_LOCAL_STORAGE_KEY } from '@/shared/features/header/components/date-time-format/date-time-format.constants';
import { TPaginatedResponse } from '@/shared/types/api.types';
import { TVersionMetadata } from '@/shared/types/projects.types';
import { BillingTimeInterval } from '@/shared/types/types.generated';

/**
 * Derives a date-fns compatible format string from the browser's locale.
 * Returns undefined if it can't be determined (so callers can fall back).
 */
export const getBrowserDateTimeFormat = (): string | undefined => {
  const locale = navigator.language || 'en-US';

  try {
    // Use an unambiguous reference date: 2033-01-02 15:04:05
    const refDate = new Date(2033, 0, 2, 15, 4, 5);

    const dateParts = new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(refDate);

    const timeParts = new Intl.DateTimeFormat(locale, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: undefined, // let the locale decide
    }).formatToParts(refDate);

    // --- Date tokens ---
    const dateTokenMap: Record<string, string> = {
      year: 'yyyy',
      month: 'MM',
      day: 'dd',
    };

    const dateFormat = dateParts
      .map(({ type, value }) => dateTokenMap[type] ?? value)
      .join('');

    // --- Time tokens ---
    const uses12h = timeParts.some(({ type }) => type === 'dayPeriod');

    const timeTokenMap: Record<string, string> = {
      hour: uses12h ? 'h' : 'HH',
      minute: 'mm',
      dayPeriod: 'a',
    };

    const timeFormat = timeParts
      .map(({ type, value }) => timeTokenMap[type] ?? value)
      .join('');

    return `${dateFormat} ${timeFormat}`;
  } catch {
    return undefined;
  }
};

export const getDefaultDateTimeFormat = (): string => {
  let defaultDateTimeFormat = 'dd/MM/yyyy HH:mm';

  try {
    const preferableDateTimeFormat = localStorage.getItem(
      DATE_TIME_FORMAT_LOCAL_STORAGE_KEY,
    );
    const testDate = format(new Date(), preferableDateTimeFormat!);

    if (testDate && testDate !== 'Invalid Date') {
      defaultDateTimeFormat = preferableDateTimeFormat!;
    }
  } catch {
    const browserFormat = getBrowserDateTimeFormat();
    if (browserFormat) {
      defaultDateTimeFormat = browserFormat;
    }
  }

  return defaultDateTimeFormat;
};

export const normalizeTimestamp = (timestamp: string | number): string => {
  if (typeof timestamp !== 'number') {
    return timestamp;
  }

  // check if timestamp is in milliseconds
  if (timestamp > 1e10) {
    return new Date(timestamp).toISOString();
  }

  // convert seconds to milliseconds
  return new Date(timestamp * 1000).toISOString();
};

export const formatDuration = (t: TFunction, seconds?: number) => {
  if (!seconds || seconds < 0) {
    return '';
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  const parts = [];

  if (hours > 0) {
    parts.push(`${hours}${t('common:time_units.hours')}`);
  }

  if (minutes > 0) {
    parts.push(`${minutes}${t('common:time_units.minutes')}`);
  }

  if (remainingSeconds > 0 || parts.length === 0) {
    parts.push(`${remainingSeconds}${t('common:time_units.seconds')}`);
  }

  return parts.join(' ');
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) {
    return '0 B';
  }
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

export const formatNumber = (number?: number) => {
  const browserLocale = navigator.language || 'en-US';

  const isUS = browserLocale === 'en-US';
  const locale = isUS ? 'en-US' : 'en-GB';

  return new Intl.NumberFormat(locale).format(number || 0);
};

// Built once — it is called per table cell on every render.
const compactCountFormatter = new Intl.NumberFormat(
  navigator.language || 'en-US',
  {
    notation: 'compact',
    maximumFractionDigits: 1,
  },
);

/**
 * Compact occurrence count for glue-log issue rollups, e.g. `3.1k`. The counts
 * are approximate, but that's conveyed by the column tooltip rather than a
 * glyph (an `≈`/`≥` prefix read as visual noise in the table).
 */
export const formatApproximateCount = (count: number): string =>
  compactCountFormatter.format(count).toLowerCase();

export const formatVersionId = (versionMetadata?: TVersionMetadata) => {
  if (!versionMetadata?.versionId) {
    return '';
  }
  if (!versionMetadata?.versionNumber) {
    return `#${versionMetadata.versionId.slice(0, 8).toUpperCase()}`;
  }
  return `#${versionMetadata.versionId.slice(0, 8).toUpperCase()} (v${versionMetadata.versionNumber})`;
};

export const formatShortId = (id?: string) => {
  if (!id) {
    return '';
  }
  return `#${id.slice(0, 8).toUpperCase()}`;
};

/**
 * Extracts and combines data from multiple paginated response sources
 * @param sources - An object with keys representing data sources and values being TPaginatedResponse objects
 * @returns A combined data object with merged items and summed counts and pages
 */
export const extractAndCombineData = (
  sources: Record<string, TPaginatedResponse<any> | undefined>,
) => {
  // First, extract data from all sources
  const extractedData = Object.entries(sources).map(([_, source]) => ({
    items: source?.items || [],
    itemCount: source?.itemCount ?? 0,
    pages: source?.pages ?? 0,
  }));

  // Then combine all the extracted data
  return {
    items: extractedData.flatMap((data) => data.items),
    itemCount: extractedData.reduce((sum, data) => sum + data.itemCount, 0),
    pages: extractedData.reduce((sum, data) => sum + data.pages, 0),
  };
};

export const capitalizeString = (text: string) => {
  return text.charAt(0).toUpperCase() + text.toLowerCase().slice(1);
};

export const getFullname = (user?: {
  firstName: string;
  lastName: string;
}): string => {
  if (!user) {
    return '';
  }

  return `${user.firstName} ${user.lastName}`;
};

export const formatTimeRemaining = (milliseconds: number) => {
  if (milliseconds <= 0) {
    return '00:00';
  }

  const totalSeconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

export const convertDateFormat = (
  dateString: string,
  endOfDay?: boolean,
): string => {
  const day = dayjs(dateString);
  const endOfPreviousDay = day.endOf('day');
  const date = endOfDay ? endOfPreviousDay : day;

  return date.utc().format('YYYY-MM-DDTHH:mm:ss.SSS000+00:00');
};

export const getTimezoneLabel = () => {
  const offsetMinutes = new Date().getTimezoneOffset();
  const offsetHours = -offsetMinutes / 60;
  return `(UTC${offsetHours >= 0 ? '+' : ''}${offsetHours})`;
};

export const formatDurationString = (
  duration: string,
  t: (key: string) => string,
): string => {
  if (!duration) {
    return '';
  }

  try {
    const [timePart] = duration.split('.');
    const [hours, minutes, seconds] = timePart.split(':').map(Number);

    const parts: string[] = [];

    if (hours > 0) {
      parts.push(
        t('common:time_units_long.hours').toLowerCase() === 'hours'
          ? `${hours} ${hours === 1 ? 'hour' : 'hours'}`
          : `${hours} ${t('common:time_units_long.hours')}`,
      );
    }

    if (minutes > 0) {
      parts.push(
        t('common:time_units_long.minutes').toLowerCase() === 'minutes'
          ? `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`
          : `${minutes} ${t('common:time_units_long.minutes')}`,
      );
    }

    if (seconds > 0 || parts.length === 0) {
      parts.push(
        t('common:time_units_long.seconds').toLowerCase() === 'seconds'
          ? `${seconds} ${seconds === 1 ? 'second' : 'seconds'}`
          : `${seconds} ${t('common:time_units_long.seconds')}`,
      );
    }

    return parts.join(', ');
  } catch (error) {
    console.error('Error formatting duration:', error);
    return duration;
  }
};

export const isoDateToBillingTs = (date: string): number => {
  return new Date(date).getTime() / 1000;
};

export const formatTimeUnit = (duration?: BillingTimeInterval) => {
  if (!duration) return null;

  // Convert everything to seconds first
  const totalSeconds =
    (duration.seconds || 0) +
    (duration.minutes || 0) * 60 +
    (duration.hours || 0) * 3600 +
    (duration.days || 0) * 86400 +
    (duration.months || 0) * 2592000 + // 30 days
    (duration.years || 0) * 31536000; // 365 days

  if (totalSeconds === 0) return null;

  // Define time units in descending order with their conversion factors
  const timeUnits: Array<[key: string, seconds: number]> = [
    ['years', 31536000],
    ['months', 2592000],
    ['days', 86400],
    ['hours', 3600],
    ['minutes', 60],
    ['seconds', 1],
  ];

  // Find the largest unit that gives us a meaningful value (>= 1)
  for (const [key, secondsPerUnit] of timeUnits) {
    const count = Math.round(totalSeconds / secondsPerUnit);
    if (count >= 1) {
      return { key, count };
    }
  }

  return null;
};
